import { useState } from "react";
import { useParams } from "react-router";
import { Box, Heading, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import CustomBox from "../../components/Box";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import useModal from "../../components/Modal/hooks/useModal";
import useGetReconciliation from "../../api/useGetReconciliation";
import { typedSxMap } from "../../utils/typedSxMap";
import { breakpoints } from "../pipelineImport/components/utils/breakpoints";
import { getStatusBadge } from "../pipelineImports/utils/pipelineImportsTable";
import ActionModal from "../reconciliations/components/actionModal";
import ReportReconciliationForm from "../reconciliations/components/reportReconciliationForm";
import OverviewSection from "./components/OverviewSection";
import OverviewTable from "./components/OverviewTable";
import CompleteReconciliationForm from "./components/completeReconciliationForm";
import RerunReconciliationForm from "./components/rerunReconciliationForm";

const styles = typedSxMap({
  container: {
    flexGrow: 1,
    height: "calc(100% + var(--m))",
    display: "flex",
    flexDirection: "column",
    marginBottom: "-30px",
  },
  headingWrapper: {
    marginBottom: "1rem",
  },
  title: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "var(--m-xs)",
    [breakpoints.md]: {
      flexDirection: "row",
      gap: "var(--m-xs)",
    },
  },
  mainText: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    width: "100%",
    [breakpoints.md]: {
      width: "calc(100% - 500px)",
    },
  },
  headingTitle: {
    fontSize: "var(--font-size-xxl)",
    fontWeight: 600,
  },
  divider: {
    borderColor: "var(--color-border)",
    margin: "var(--m-s) 0",
  },
  tabs: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  tabPanels: {
    flexGrow: 1,
  },
  tabPanel: {
    height: "100%",
    padding: "var(--m-s) 0 0 0",
  },
  tabList: {
    borderBottom: "1px solid var(--color-border)",
    "& button": {
      color: "var(--color-text-soft)",
      padding: "var(--m-xxs) var(--m-s)",
      "&:hover": {
        color: "var(--color-text)",
        borderColor: "var(--color-primary)",
      },
      "&[aria-selected=true]": {
        color: "var(--color-text)",
        borderColor: "var(--color-primary)",
        backgroundColor: "transparent",
      },
    },
  },
  badge: {
    textTransform: "capitalize",
    fontSize: "var(--font-size-s)",
  },
  actions: {
    display: "flex",
    gap: "var(--m-s)",
    marginLeft: "auto",
  },
  mainActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    overflow: "auto",
    justifyContent: "flex-end",
    alignSelf: "flex-end",
  },
});

export default function Reconciliation() {
  const { reconciliationId } = useParams();
  const { data: reconciliation, isLoading } = useGetReconciliation(reconciliationId ?? "");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const completeModal = useModal();
  const rerunModal = useModal();

  const handleOpenInSheets = () => {
    if (reconciliation?.drive_file_id) {
      window.open(`https://docs.google.com/spreadsheets/d/${reconciliation.drive_file_id}`, "_blank");
    }
  };

  const getTitle = (reconciliation?: any) => {
    return `${reconciliation?.name} - Reconciliation`;
  };

  if (isLoading) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.headingWrapper}>
        <Box sx={styles.title}>
          <Box sx={styles.mainText}>
            <Heading size="m" sx={styles.headingTitle} as="h1">
              {getTitle(reconciliation)}
            </Heading>
            {getStatusBadge(reconciliation?.status, reconciliation?.error || "")}
          </Box>
          <Box sx={styles.mainActions}>
            <Button variants={["secondary"]} onClick={() => rerunModal.setOpen(true)} disabled={!reconciliation}>
              Rerun
            </Button>
            <Button variants={["secondary"]} onClick={() => setIsModalOpen(true)}>
              Report Issue
            </Button>
            {/* <Button variants={["secondary"]} onClick={() => {}}>
              Download CSV
            </Button> */}
            <Button variants={["secondary"]} onClick={handleOpenInSheets} disabled={!reconciliation?.drive_file_id}>
              Open Sheets
            </Button>
            <Button variants={["primary"]} onClick={() => completeModal.setOpen(true)} disabled={isLoading || reconciliation?.status !== "review"}>
              Mark Complete
            </Button>
          </Box>
        </Box>
      </Box>

      <ActionModal isOpen={isModalOpen} workspaceId="" reconciliation={reconciliation} title="Report an Issue" onClose={() => setIsModalOpen(false)}>
        <ReportReconciliationForm workspaceId="" reconciliation={reconciliation} onSuccess={() => setIsModalOpen(false)} action="report" />
      </ActionModal>

      <Tabs sx={styles.tabs}>
        <TabList sx={styles.tabList}>
          <Tab>Overview</Tab>
          <Tab>Data</Tab>
        </TabList>

        <TabPanels sx={styles.tabPanels}>
          <TabPanel sx={styles.tabPanel}>{reconciliation && <OverviewSection reconciliation={reconciliation} />}</TabPanel>
          <TabPanel sx={styles.tabPanel}>
            {reconciliation && reconciliation?.drive_file_id ? (
              <Box width="100%" height="calc(100% - var(--m))">
                <iframe
                  title="Reconciliation Data Sheet"
                  src={`https://docs.google.com/spreadsheets/d/${reconciliation?.drive_file_id}/htmlembed?chrome=false`}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "1px solid var(--color-border)",
                  }}
                />
              </Box>
            ) : (
              <Box p="var(--m-s)">No data available</Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {completeModal.openDelayed && (
        <Modal {...completeModal} useBox={false} useCloseButton>
          <CustomBox>
            <CompleteReconciliationForm
              reconciliation={reconciliation}
              onSuccess={() => completeModal.handleClose()}
              onClose={completeModal.handleClose}
            />
          </CustomBox>
        </Modal>
      )}

      {rerunModal.openDelayed && (
        <Modal {...rerunModal} useBox={false} useCloseButton>
          <CustomBox>
            <RerunReconciliationForm reconciliation={reconciliation} onSuccess={() => rerunModal.handleClose()} onClose={rerunModal.handleClose} />
          </CustomBox>
        </Modal>
      )}
    </Box>
  );
}
